import React, { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  UncontrolledTooltip,
} from "reactstrap"

// Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Hooks
import {
  usePackages,
  useCreatePackage,
  useEditPackage,
  useDeletePackage,
} from "hooks/packageHook"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"
import { useModels } from "hooks/modelHook"
import { modelToModelIdsNumber } from "utils/modelUtils"
import BadgeList from "components/Common/BadgeList"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"

const PackageList = () => {
  document.title = "Package List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [packageData, setPackageData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      with_models: true,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: packagesResponse,
    isLoading,
    refetch: packageRefetch,
    isRefetching: packageRefetchLoading,
  } = usePackages(queryParams)

  const { data: modelsResponse, isLoading: modelsFetchLoading } = useModels(
    { all: true },
    modal
  )

  const { mutateAsync: createPackage, isPending: packageCreateLoading } =
    useCreatePackage()
  const { mutateAsync: editPackage, isPending: packageEditLoading } =
    useEditPackage()
  const { mutateAsync: deletePackage, isPending: packageDeleteLoading } =
    useDeletePackage()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setPackageData(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setPackageData(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handlePackageClick = useCallback(
    data => {
      setPackageData(data)
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(data => {
    setPackageData(data)
    setDeleteModal(true)
  }, [])

  const handleDeletePackage = useCallback(async () => {
    if (packageData?.id) {
      try {
        const res = await deletePackage(packageData.id)
        toast.success(res?.message ?? "Package deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        toast.error("Failed to delete package.")
      }
    }
  }, [packageData, deletePackage])

  const handlePackageListRefresh = () => packageRefetch()

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(1, "Name must be at least 1 characters long")
      .required("Please enter package name"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name,
        modelIds: values.selectedModels?.map(n => Number(n)),
      }
      const action = isEdit
        ? editPackage({ id: packageData.id, ...payload })
        : createPackage(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Package ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        toast.error("Failed to save package.")
      }
    },
    [isEdit, packageData, editPackage, createPackage, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: useMemo(
      () => ({
        name: packageData?.name || "",
        selectedModels: modelToModelIdsNumber(packageData?.models) || [],
      }),
      [packageData]
    ),
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: ({ row }) => (
          <Link to="#" className="text-body fw-bold">
            {row.original.id}
          </Link>
        ),
      },
      { header: "Name", accessorKey: "name" },
      { header: "Slug", accessorKey: "slug" },
      {
        header: "Models",
        accessorKey: "models",
        cell: ({ row }) => <BadgeList items={row.original.models} />,
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) => formatReadableDate(row.original.created_at),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handlePackageClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handlePackageClick, onClickDelete]
  )

  const packagesData = packagesResponse?.data?.data || []
  const paginationInfo = packagesResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeletePackage}
        loading={packageDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Package" breadcrumbItem="Package List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom d-flex align-items-center justify-content-between">
                  <h5 className="mb-0 card-title">Package Lists</h5>
                  <div>
                    <Button
                      color="primary"
                      onClick={handleAddNewClick}
                      className="me-1"
                    >
                      Add New Package
                    </Button>
                    <Button
                      color="light"
                      onClick={handlePackageListRefresh}
                      className="me-1"
                      id="refreshTooltip"
                    >
                      <i
                        className={`mdi mdi-refresh ${
                          packageRefetchLoading ? "mdi-spin" : ""
                        }`}
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target="refreshTooltip"
                      >
                        Refresh
                      </UncontrolledTooltip>
                    </Button>
                    {hasActiveFilters && (
                      <Button
                        color="danger"
                        onClick={resetParams}
                        className="me-1"
                        id="resetTooltip"
                      >
                        <i className="mdi mdi-close" />
                        <UncontrolledTooltip
                          placement="top"
                          target="resetTooltip"
                        >
                          Reset Filters
                        </UncontrolledTooltip>
                      </Button>
                    )}
                  </div>
                </CardBody>

                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>

                  <TableContainer
                    columns={columns}
                    data={packagesData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || packageRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Package" : "Add Package"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <div className="mb-3">
                  <Label htmlFor="name">Package Name</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Enter package name"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name && (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label>Select Models for This Package</Label>
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={modelsResponse?.data}
                      name="selectedModels"
                      selected={validation.values.selectedModels}
                      onChange={newSelected =>
                        validation.setFieldValue("selectedModels", newSelected)
                      }
                      loading={modelsFetchLoading}
                    />
                  </div>
                </div>

                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={packageCreateLoading || packageEditLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Package"}
                    {(packageCreateLoading || packageEditLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default PackageList
